import React, { Component, createRef, useState } from 'react';
import { registerOrderParcelInWarehouseByParcelId } from 'fetch/fetchWarehouseOrders';
import { Input } from '../../components/QrScanner/InputScanner';
import { Button } from '../../components/buttons/Button';
import { registerParcelInHub } from '../../fetch/registerParcelInHub';
import { vibrate } from 'utils/safeVibrate';
import { MdDone } from 'react-icons/md';
import {
  HubSortInstruction,
  OrderStatus,
  Region,
} from '../../types/orderTypes';
import { fetchHubSortScan } from './HubUtils';
import scanPerformance from '../../utils/scanPerformance';
import { Loader } from '../../components/Loader/Loader';
import { play, preload } from '../../components/QrScanner/scannerSounds';
import { QrShelfScanner } from '../../components/QrScanner/QrShelfScanner';
import { QrOrderIdScanner } from '../../components/QrScanner/QrOrderIdScanner';
import {
  ScannerMode,
  ScanningState,
} from '../../components/QrScanner/scanningTypes';
import { ScannerFeedbackBox } from '../../components/QrScanner/ScannerFeedbackBox';
import { Helmet } from 'react-helmet';
import './HubScanner.scss';
import { isAndroid, isMobile } from 'react-device-detect';
import { supportsWebcam } from '../../utils/supportsWebcam';
import {
  arrayOfWarehouseLocations,
  getWarehouseNameForShelfId,
  hubWarehouseLocationsByRegion,
  warehouseLocations,
} from '../../const/HubConfig';
import { ButtonComponent } from '../../ReusableComponents/Button/ButtonComponent';

const inactiveStati = new Set<OrderStatus>([
  OrderStatus.Delivered,
  OrderStatus.Canceled,
]);

export function orderNotActive(status?: OrderStatus): boolean {
  return status && inactiveStati.has(status);
}

const ManualInput = ({
  type,
  manualMode,
  placeholder,
  setManualMode,
  onAccept = () => {},
}: {
  type: string;
  manualMode: boolean;
  placeholder: string;
  setManualMode: (manualMode: boolean) => void;
  onAccept: (value: string) => void;
}) => {
  const [textInput, setTextInput] = useState('');

  const handleClick = e => {
    setManualMode(false);
    onAccept(textInput);
    setTextInput('');
  };

  const activate = () => {
    setManualMode(true);
  };

  return (
    <div
      style={{
        width: '100%',
        textAlign: 'center',
      }}
    >
      {!manualMode && (
        <ButtonComponent onClick={activate} className="manual-button">
          Manual
        </ButtonComponent>
      )}
      {manualMode && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            margin: '0 30px',
          }}
        >
          <Input
            type={type}
            placeholder={placeholder}
            value={textInput}
            isFocused={manualMode}
            onChange={e => setTextInput(e.target.value)}
          />
          <ButtonComponent
            className="manual-confirmation"
            onClick={handleClick}
          >
            <MdDone />
          </ButtonComponent>
        </div>
      )}
    </div>
  );
};

interface HubScannerState {
  setShelfId: boolean;
  shelfId: string;
  orderIdForShelfId: string;
  orderScannerType: boolean;
  freshScannerState: boolean;
  parcelId: string;
  scannerReady: boolean;
  loading: boolean;
  shelfError: boolean;
  storedInWarehouse: boolean;
  isWarehouseScan: boolean;
  differentRegionScanned?: Region;
  unknownOrder: boolean;
  manualInputOpen: boolean;
  orderIdForRescan?: string;
  scannerMode: ScannerMode;
  scannerIsOpen: boolean;
  networkError: boolean;
  loaderMessage?: string;
  warehouseLocationBasedOnSelectedRegion: string;
  orderDeliveredOrCanceled: boolean;
}

interface HubScannerProps {
  region: Region;
}

export default class HubScanner extends Component<
  HubScannerProps,
  HubScannerState
> {
  constructor(props: HubScannerProps) {
    super(props);

    this.state = {
      setShelfId: false,
      shelfId: '',
      orderIdForShelfId: '',
      orderScannerType: true,
      freshScannerState: true,
      parcelId: '',
      scannerReady: true,
      loading: false,
      shelfError: false,
      isWarehouseScan: true,
      storedInWarehouse: false,
      differentRegionScanned: null,
      unknownOrder: false,
      manualInputOpen: false,
      orderIdForRescan: undefined,
      scannerMode:
        (isMobile && isAndroid) || !supportsWebcam ? 'laser' : 'camera',
      scannerIsOpen: false,
      networkError: false,
      warehouseLocationBasedOnSelectedRegion: hubWarehouseLocationsByRegion(
        props.region
      ),
      orderDeliveredOrCanceled: false,
    };
  }

  scannerInputRef = createRef<HTMLInputElement>();
  scannerInputBuffer = '';

  componentDidMount = (): void => {
    preload();
  };

  shelfErrorTimeout = undefined;

  fetchShelfIdAndRegion = async (
    orderId: string,
    parcelId?: string
  ): Promise<HubSortInstruction> => {
    this.setState({ loading: true, loaderMessage: 'Fetching shelf...' });

    const response = await fetchHubSortScan(orderId, parcelId, true);
    this.setState({ loading: false, loaderMessage: null });
    return response;
  };

  checkOrderIdInHub = async (
    orderId: string,
    scannerIsOpen: boolean,
    parcelId: string
  ): Promise<boolean> => {
    if ((orderId || '').trim() === '') {
      return;
    }
    scanPerformance.startedBundleFetch(orderId);
    this.setState({
      orderIdForShelfId: orderId,
      parcelId: parcelId,
      storedInWarehouse: false,
      shelfId: '',
      setShelfId: false,
      unknownOrder: false,
      orderIdForRescan: undefined,
      scannerIsOpen,
      networkError: false,
      orderDeliveredOrCanceled: false,
    });

    const response = await this.fetchShelfIdAndRegion(orderId, parcelId);
    scanPerformance.completedBundleFetch(orderId);

    if (!!response) {
      if (response.orderRegion !== this.props.region) {
        this.setState({
          shelfId: `to:${response.orderRegion}`,
          setShelfId: false,
          orderScannerType: false,
          freshScannerState: false,
          isWarehouseScan: !response.orderRegion,
          differentRegionScanned: response.orderRegion,
          unknownOrder: !response.orderPresent,
          orderDeliveredOrCanceled: orderNotActive(response.orderStatus),
        });
        play('orderSuccess');
        return;
      }
      if (!!response.shelfId) {
        this.setState({
          shelfId: response.shelfId,
          setShelfId: false,
          orderScannerType: false,
          freshScannerState: false,
          isWarehouseScan: false,
          differentRegionScanned: null,
          orderDeliveredOrCanceled: orderNotActive(response.orderStatus),
        });
        play('orderSuccess');
      } else {
        this.setState({
          shelfId: '',
          setShelfId: false,
          orderScannerType: true,
          freshScannerState: false,
          isWarehouseScan: true,
          differentRegionScanned: null,
          unknownOrder: !response.orderPresent,
          orderDeliveredOrCanceled: orderNotActive(response.orderStatus),
        });
        play('orderFailure');
        return false;
      }
    } else {
      this.setState({
        orderIdForShelfId: '',
        shelfId: '',
        setShelfId: false,
        orderScannerType: true,
        freshScannerState: true,
        isWarehouseScan: true,
        differentRegionScanned: null,
        networkError: true,
        orderDeliveredOrCanceled: false,
      });
      play('scannerBlocked');
      return false;
    }
    vibrate(300);
  };

  confirmOrderAtShelfInHub = async (
    confirmedShelfId: string,
    scannerIsOpen = false
  ): Promise<void> => {
    this.setState({
      shelfError: false,
      orderIdForRescan: undefined,
      networkError: false,
    });
    if (
      this.state.shelfId &&
      String(this.state.shelfId).toUpperCase() ===
        confirmedShelfId.toUpperCase()
    ) {
      scanPerformance.startedConfirmShelf(confirmedShelfId);
      this.setState({
        setShelfId: false,
        loading: true,
        loaderMessage: 'Confirming shelf...',
      });
      try {
        const ok = await registerParcelInHub(
          this.state.orderIdForShelfId,
          confirmedShelfId,
          this.state.parcelId,
          this.state.warehouseLocationBasedOnSelectedRegion,
          this.props.region
        );
        scanPerformance.completedConfirmShelf(confirmedShelfId);
        if (ok) {
          this.setState({
            setShelfId: true,
            orderScannerType: true,
            loading: false,
            loaderMessage: null,
            isWarehouseScan: false,
            differentRegionScanned: null,
            scannerIsOpen,
          });
          vibrate(300);
          play('shelfSuccess');
        } else {
          this.setState({
            loading: false,
            loaderMessage: null,
            networkError: true,
          });
        }
      } catch (error) {
        console.error('Error: Confirming order shelf failed: ', error);
        this.setState({
          loading: false,
          loaderMessage: null,
          networkError: true,
        });
      }
    } else {
      registerParcelInHub(
        this.state.orderIdForShelfId,
        confirmedShelfId,
        this.state.parcelId,
        this.state.warehouseLocationBasedOnSelectedRegion,
        this.props.region
      ).then(() => {
        // not waiting for a reply here, to continue faster
      });
      if (this.shelfErrorTimeout) {
        window.clearTimeout(this.shelfErrorTimeout);
      }
      play('shelfFailure');
      this.setState({
        shelfError: true,
        orderScannerType: false,
      });
      this.shelfErrorTimeout = window.setTimeout(() => {
        this.setState({ shelfError: false });
        this.shelfErrorTimeout = undefined;
      }, 500);
    }
  };

  restartProcess = (): void => {
    this.setState({
      orderScannerType: true,
      freshScannerState: true,
      shelfId: '',
      orderIdForShelfId: '',
      shelfError: false,
      isWarehouseScan: false,
      setShelfId: false,
      differentRegionScanned: null,
      orderIdForRescan: undefined,
      networkError: false,
      orderDeliveredOrCanceled: false,
    });
    if (this.scannerInputRef && this.scannerInputRef.current) {
      this.scannerInputRef.current.focus();
    }
  };

  parseJsonData(data: string) {
    try {
      return JSON.parse(data);
    } catch (e) {
      return undefined;
    }
  }

  orderIdChanged = (orderId: string) => {
    this.setState({ orderIdForRescan: orderId });
    play('orderFailure');
  };

  handleLaserScannerInput = (
    e: React.ChangeEvent<HTMLInputElement>
  ): string => {
    const newInput = String(e.target.value);
    console.log(`captured input ${newInput}`);
    if (newInput.endsWith('}')) {
      const fullInput = this.scannerInputBuffer + newInput;
      this.scannerInputBuffer = '';
      return fullInput;
    }
    this.scannerInputBuffer += newInput;
    return null;
  };

  onOrderInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!this.state.loading) {
      this.setState({ networkError: false });
      const input = this.handleLaserScannerInput(e);
      if (input && input.startsWith('{') && input.endsWith('}')) {
        vibrate(100);
        const orderData = this.parseJsonData(input);
        console.log('stringify', JSON.stringify(orderData));
        if (orderData.orderId) {
          if (
            !!!this.state.orderIdForShelfId ||
            this.state.orderIdForShelfId === orderData.orderId ||
            this.state.orderIdForRescan === orderData.orderId ||
            this.state.setShelfId ||
            this.state.differentRegionScanned ||
            this.state.orderDeliveredOrCanceled
          ) {
            await this.checkOrderIdInHub(
              orderData.orderId,
              false,
              orderData.parcelId
            );
          } else {
            this.orderIdChanged(orderData.orderId);
          }
        } else if (
          orderData.shelfId &&
          arrayOfWarehouseLocations().includes(
            orderData.shelfId.toLowerCase()
          ) &&
          this.state.isWarehouseScan
        ) {
          const { orderIdForShelfId, parcelId } = this.state;

          await this.storeInWareHouseAction(
            orderIdForShelfId,
            parcelId,
            orderData.shelfId
          );
        }
      }
    } else {
      play('scannerBlocked');
    }
  };

  storeInWareHouseAction = async (
    orderId: string,
    parcelId: string,
    shelfId: string
  ) => {
    this.setState({
      loading: true,
      loaderMessage: 'Confirming warehouse...',
      orderIdForRescan: undefined,
      networkError: false,
    });

    try {
      let warehouseName;
      if (shelfId.toLowerCase() === 'warehouse') {
        warehouseName = warehouseLocations.OSLO.ALNA;
      } else {
        warehouseName = getWarehouseNameForShelfId(shelfId);
      }

      const response = await registerOrderParcelInWarehouseByParcelId(
        orderId,
        parcelId,
        warehouseName
      );
      this.setState({ loading: false, loaderMessage: null });

      if (response.ok) {
        this.setState({
          orderScannerType: true,
          freshScannerState: false,
          shelfId: '',
          shelfError: false,
          isWarehouseScan: false,
          storedInWarehouse: true,
          setShelfId: false,
          orderIdForShelfId: '',
        });
        vibrate(300);
        play('shelfSuccess');
      } else {
        this.setState({
          networkError: true,
          loading: false,
          loaderMessage: null,
        });
      }
    } catch (error) {
      console.error('Error: Confirming order in warehouse failed: ', error);
      this.setState({
        networkError: true,
        loading: false,
        loaderMessage: null,
      });
    }
  };

  onShelfInputChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!this.state.loading) {
      this.setState({ networkError: false, orderDeliveredOrCanceled: false });
      const input = this.handleLaserScannerInput(e);
      if (input && input.startsWith('{') && input.endsWith('}')) {
        vibrate(100);
        const jsonData = this.parseJsonData(input);
        console.log(JSON.stringify(jsonData));
        if (jsonData.orderId) {
          if (
            jsonData.orderId === this.state.orderIdForShelfId ||
            jsonData.orderId === this.state.orderIdForRescan
          ) {
            await this.checkOrderIdInHub(
              jsonData.orderId,
              false,
              jsonData.parcelId
            );
          } else {
            this.orderIdChanged(jsonData.orderId);
          }
        } else if (jsonData.shelfId) {
          const { orderIdForShelfId, parcelId } = this.state;
          if (
            arrayOfWarehouseLocations().includes(jsonData.shelfId) &&
            this.state.isWarehouseScan
          ) {
            await this.storeInWareHouseAction(
              orderIdForShelfId,
              parcelId,
              jsonData.shelfId
            );
          } else {
            await this.confirmOrderAtShelfInHub(jsonData.shelfId);
          }
        }
      }
    } else {
      play('scannerBlocked');
    }
  };

  switchScannerMode = (currentMode: ScannerMode) => {
    if (currentMode === 'laser') {
      this.setState({ scannerMode: 'camera' });
    } else {
      this.setState({ scannerMode: 'laser' });
      if (this.scannerInputRef && this.scannerInputRef.current) {
        this.scannerInputRef.current.focus();
      }
    }
  };

  LaserScannerUI = ({ scanningState }: { scanningState: ScanningState }) => {
    const {
      shelfError,
      orderScannerType,
      orderIdForShelfId,
      loading,
      scannerReady,
      shelfId,
      setShelfId,
      unknownOrder,
      differentRegionScanned,
      storedInWarehouse,
      manualInputOpen,
      orderIdForRescan,
      networkError,
      loaderMessage,
    } = this.state;

    return (
      <div className="scanner-laser">
        <div className="scannerInput">
          {networkError && (
            <div className="scannerInput__networkError">
              Network Error! Scan again to rety!
            </div>
          )}
          <ScannerFeedbackBox
            loading={loading}
            scannerReady={scannerReady}
            shelfId={shelfId}
            orderIdForShelfId={orderIdForShelfId}
            setShelfId={setShelfId}
            unknownOrder={unknownOrder}
            differentRegionScanned={differentRegionScanned}
            storedInWarehouse={storedInWarehouse}
            manualInputOpen={manualInputOpen}
            orderIdForRescan={orderIdForRescan}
            scannerMode="laser"
            scanningState={scanningState}
          />
          <div className="scannerInputField">
            <input
              type="text"
              className="form-control"
              onChange={
                orderScannerType === true
                  ? this.onOrderInputChange
                  : this.onShelfInputChange
              }
              autoFocus={true}
              value=""
              onBlur={() => this.setState({ scannerReady: false })}
              onFocus={() => this.setState({ scannerReady: true })}
              ref={this.scannerInputRef}
            />
          </div>
          {loading && (
            <div className="scannerInput__loading">
              <Loader type="spinner" size="xxl" />
              {loaderMessage && (
                <span className="loading-message">{loaderMessage}</span>
              )}
            </div>
          )}
          {shelfError && <div className="scannerInput__error-flashing" />}
        </div>
        <div style={{ height: '20px' }} />
        {scanningState === 'confirm-warehouse' ? null : scanningState ===
          'confirm-shelf' ? (
          <ManualInput
            placeholder="Shelf Letter"
            manualMode={manualInputOpen}
            type="text"
            setManualMode={manualMode =>
              this.setState({ manualInputOpen: manualMode })
            }
            onAccept={value => {
              if (this.scannerInputRef && this.scannerInputRef.current) {
                this.scannerInputRef.current.focus();
              }
              this.confirmOrderAtShelfInHub(value.toUpperCase());
            }}
          />
        ) : (
          <ManualInput
            placeholder="Order ID"
            manualMode={manualInputOpen}
            setManualMode={manualMode =>
              this.setState({ manualInputOpen: manualMode })
            }
            type="tel"
            onAccept={value => {
              if (this.scannerInputRef && this.scannerInputRef.current) {
                this.scannerInputRef.current.focus();
              }
              this.checkOrderIdInHub(value, false, '');
            }}
          />
        )}
      </div>
    );
  };

  CameraScannerUI = ({ scanningState }: { scanningState: ScanningState }) => {
    const {
      orderIdForShelfId,
      parcelId,
      scannerIsOpen,
      loading,
      scannerReady,
      shelfId,
      setShelfId,
      unknownOrder,
      differentRegionScanned,
      storedInWarehouse,
      manualInputOpen,
      orderIdForRescan,
      networkError,
    } = this.state;
    return (
      <div className="scanner-camera">
        {networkError && (
          <div className="scanner-camera__networkError">
            Network Error! Scan & confirm again to rety!
          </div>
        )}
        {scanningState === 'confirm-warehouse' ? (
          <QrShelfScanner
            titleButton={`CONFIRM WAREHOUSE`}
            handleWarehouseScan={this.storeInWareHouseAction}
            orderIdForShelfId={orderIdForShelfId}
            parcelId={parcelId}
            scannerIsOpen={scannerIsOpen}
            isScanWarehouse={true}
          />
        ) : scanningState === 'confirm-shelf' ? (
          <QrShelfScanner
            titleButton={`CONFIRM SHELF ${this.state.shelfId}`}
            handleClickButton={this.confirmOrderAtShelfInHub}
            scannerIsOpen={scannerIsOpen}
          />
        ) : scanningState === 'confirm-linehaul' ? (
          <QrShelfScanner
            titleButton={`CONFIRM REGION ${this.state.differentRegionScanned}`}
            handleClickButton={this.confirmOrderAtShelfInHub}
            scannerIsOpen={scannerIsOpen}
          />
        ) : (
          <QrOrderIdScanner
            titleButton={'CONFIRM PACKAGE'}
            handleClickButton={this.checkOrderIdInHub}
            scannerIsOpen={scannerIsOpen}
          />
        )}
        <div className="scannerInput">
          <ScannerFeedbackBox
            loading={loading}
            scannerReady={scannerReady}
            shelfId={shelfId}
            orderIdForShelfId={orderIdForShelfId}
            setShelfId={setShelfId}
            unknownOrder={unknownOrder}
            differentRegionScanned={differentRegionScanned}
            storedInWarehouse={storedInWarehouse}
            manualInputOpen={manualInputOpen}
            orderIdForRescan={orderIdForRescan}
            scannerMode="camera"
            scanningState={scanningState}
          />
          {loading && (
            <div className="scannerInput__loading">
              <Loader type="spinner" size="xxl" />
            </div>
          )}
        </div>
      </div>
    );
  };

  render() {
    const {
      orderScannerType,
      freshScannerState,
      parcelId,
      isWarehouseScan,
      differentRegionScanned,
      scannerMode,
      orderDeliveredOrCanceled,
      unknownOrder,
    } = this.state;

    let scanningState: ScanningState;
    if (orderDeliveredOrCanceled) {
      scanningState = 'delivered-or-canceled';
    } else if (orderScannerType === true) {
      if (freshScannerState === false && parcelId && isWarehouseScan) {
        scanningState = 'confirm-warehouse';
      } else {
        scanningState = 'confirm-package';
      }
    } else {
      if (unknownOrder) {
        scanningState = 'confirm-warehouse';
      } else if (differentRegionScanned) {
        scanningState = 'confirm-linehaul';
      } else {
        scanningState = 'confirm-shelf';
      }
    }

    return (
      <div className="hubScanner">
        <Helmet>
          <title>Hub Scanner</title>
        </Helmet>
        {scannerMode === 'laser' ? (
          <this.LaserScannerUI scanningState={scanningState} />
        ) : (
          <this.CameraScannerUI scanningState={scanningState} />
        )}
        <div className="scanner-spacer" />
        <div className="scanmodecontrol">
          <Button
            className="scanmodecontrol__button"
            title={scannerMode === 'laser' ? 'Use Camera' : 'Use Laser'}
            onClick={() => this.switchScannerMode(scannerMode)}
          />
          <Button
            className="scanmodecontrol__button"
            onClick={() => this.restartProcess()}
            title="Restart"
          />
        </div>
      </div>
    );
  }
}
